import Icon, { IconType } from 'components/Icon';
import useSidebarDropdown from 'hooks/useSidebarDropdown';
import { useTheme } from 'styled-components';
import { getButtonStyle } from 'tokens';
import * as S from './styles';
import { Size } from './styles';
import type { UiColor } from 'tokens';

export interface ColorButtonMenuItem {
	label: string;
	icon?: IconType;
	onClick: () => void;
	disabled?: boolean;
}

interface ColorButtonProps {
	label: string;
	icon?: IconType;
	onClick?: () => void;
	disabled?: boolean;
	size?: Size;
	fullWidth?: boolean;
	color?: UiColor;
	// Currently uses useSidebarDropdown which auto-closes when sidebar hides.
	// This couples ColorButton to sidebar state. If menuItems is used outside the sidebar,
	// extract a generic useDropdown hook (click-outside only) and move the sidebar auto-close
	// logic into sidebar-specific consumers (e.g. Sidebar/NewButton).
	menuItems?: ColorButtonMenuItem[];
}

const ColorButton = ({
	label,
	icon,
	onClick,
	disabled = false,
	size = 'default',
	fullWidth = false,
	color = 'blue',
	menuItems,
}: ColorButtonProps) => {
	const theme = useTheme();
	const {
		isOpen: isDropdownOpen,
		toggle: onToggleDropdown,
		close,
		containerRef,
	} = useSidebarDropdown();

	const onMenuItemClick = (item: ColorButtonMenuItem) => {
		if (item.disabled) return;
		item.onClick();
		close();
	};

	const iconColor = getButtonStyle(theme, color).color;

	if (menuItems && menuItems.length > 0) {
		return (
			<S.SplitContainer ref={containerRef} $fullWidth={fullWidth} $color={color}>
				<S.SplitMain
					type="button"
					onClick={onClick}
					disabled={disabled}
					title={label}
					$color={color}
				>
					{label}
				</S.SplitMain>
				<S.SplitDropdown
					type="button"
					onClick={onToggleDropdown}
					aria-label="Show more options"
					$color={color}
				>
					<Icon type={IconType.FILLED_ARROW_DOWN} width={18} height={18} color={iconColor} />
				</S.SplitDropdown>
				{isDropdownOpen && (
					<S.DropdownMenu>
						{menuItems.map((item) => (
							<S.DropdownMenuItem
								key={item.label}
								type="button"
								disabled={item.disabled}
								aria-disabled={item.disabled}
								onClick={() => onMenuItemClick(item)}
								title={item.label}
							>
								{item.icon && (
									<Icon type={item.icon} width={14} height={14} color={theme.gray[1000]} />
								)}
								{item.label}
							</S.DropdownMenuItem>
						))}
					</S.DropdownMenu>
				)}
			</S.SplitContainer>
		);
	}

	return (
		<S.Button
			type="button"
			$size={size}
			$fullWidth={fullWidth}
			$color={color}
			onClick={onClick}
			disabled={disabled}
		>
			{icon && <Icon type={icon} width={16} height={16} color={iconColor} />}
			{label}
		</S.Button>
	);
};

export default ColorButton;
